import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
// TipTap v3 consolidated Color/FontFamily/FontSize into extension-text-style
// itself (named exports, no separate packages, and FontSize ships built in —
// no need for a hand-rolled extension the way TipTap v2 required).
import { TextStyle, Color, FontFamily, FontSize } from '@tiptap/extension-text-style';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import {
  ArrowUturnLeftIcon, ArrowUturnRightIcon, BoldIcon, ItalicIcon, UnderlineIcon,
  Bars3BottomLeftIcon, Bars3Icon, Bars3BottomRightIcon, Bars4Icon,
  ListBulletIcon, NumberedListIcon, PhotoIcon, SwatchIcon, VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { VideoEmbed } from '../utils/tiptapVideoEmbed';
import API from '../api';

const FONT_FAMILIES = [
  { label: 'Sans Serif', value: '' },
  { label: 'Serif', value: 'Georgia, serif' },
  { label: 'Monospace', value: '"Courier New", monospace' },
];
const FONT_SIZES = [
  { label: 'Small', value: '0.875rem' },
  { label: 'Normal', value: '' },
  { label: 'Large', value: '1.25rem' },
  { label: 'X-Large', value: '1.75rem' },
];
const ALIGN_OPTIONS = [
  { value: 'left', icon: Bars3BottomLeftIcon, label: 'Align left' },
  { value: 'center', icon: Bars3Icon, label: 'Align center' },
  { value: 'right', icon: Bars3BottomRightIcon, label: 'Align right' },
  { value: 'justify', icon: Bars4Icon, label: 'Justify' },
];

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClick} disabled={disabled} title={title}
      className={`p-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        active ? 'bg-primary text-white' : 'text-gray-500 hover:text-primary hover:bg-white'}`}>
      {children}
    </button>
  );
}

// A real WYSIWYG editor (TipTap/ProseMirror) rather than the earlier plain
// textarea + markdown-symbol approach — font, size, color, and alignment
// need actual rendering to be meaningfully "what you see is what you get",
// which a styled-text-in-a-<textarea> can never really deliver. Content is
// stored/returned as HTML; the public page sanitizes it before display
// (see PromotionDetail.jsx) since this HTML round-trips through the API,
// not just this editor's own constrained UI.
export default function RichTextEditor({ value, onChange }) {
  const [uploadingImage, setUploadingImage] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image,
      Link.configure({ openOnClick: false }),
      VideoEmbed,
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  if (!editor) return null;

  const handleInsertImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = '';
    setUploadingImage(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';
      editor.chain().focus().setImage({ src: `${BASE}${data.url}` }).run();
    } catch { alert('Image upload failed'); }
    setUploadingImage(false);
  };

  const handleInsertVideo = () => {
    const url = window.prompt('Paste a YouTube, Facebook, or Instagram video URL:');
    if (!url || !url.trim()) return;
    editor.chain().focus().setVideoEmbed(url.trim()).run();
  };

  const activeAlign = ALIGN_OPTIONS.find(a => editor.isActive({ textAlign: a.value }))?.value || 'left';

  return (
    <div className="border border-gray-200">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 px-2 py-1.5 bg-gray-50">
        <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <ArrowUturnLeftIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <ArrowUturnRightIcon className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <select
          onChange={e => e.target.value ? editor.chain().focus().setFontFamily(e.target.value).run() : editor.chain().focus().unsetFontFamily().run()}
          defaultValue=""
          className="text-xs border border-gray-200 px-1.5 py-1 bg-white focus:outline-none focus:border-primary" title="Font family">
          {FONT_FAMILIES.map(f => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <select
          onChange={e => e.target.value ? editor.chain().focus().setFontSize(e.target.value).run() : editor.chain().focus().unsetFontSize().run()}
          defaultValue=""
          className="text-xs border border-gray-200 px-1.5 py-1 bg-white focus:outline-none focus:border-primary" title="Font size">
          {FONT_SIZES.map(s => <option key={s.label} value={s.value}>{s.label}</option>)}
        </select>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <ToolbarButton title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <BoldIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <ItalicIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>

        <label title="Text color" className="relative p-1.5 text-gray-500 hover:text-primary hover:bg-white transition-colors cursor-pointer">
          <SwatchIcon className="w-4 h-4" />
          <input type="color" onChange={e => editor.chain().focus().setColor(e.target.value).run()}
            className="absolute inset-0 opacity-0 cursor-pointer" />
        </label>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {ALIGN_OPTIONS.map(({ value, icon: Icon, label }) => (
          <ToolbarButton key={value} title={label} active={activeAlign === value}
            onClick={() => editor.chain().focus().setTextAlign(value).run()}>
            <Icon className="w-4 h-4" />
          </ToolbarButton>
        ))}

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <ListBulletIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <NumberedListIcon className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <label title="Insert image" className={`p-1.5 transition-colors ${uploadingImage ? 'text-gray-300' : 'text-gray-500 hover:text-primary hover:bg-white cursor-pointer'}`}>
          <PhotoIcon className="w-4 h-4" />
          <input type="file" accept="image/*" onChange={handleInsertImage} disabled={uploadingImage} className="hidden" />
        </label>
        <ToolbarButton title="Insert video (YouTube, Facebook, or Instagram URL — no upload, no storage used)" onClick={handleInsertVideo}>
          <VideoCameraIcon className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Editable area */}
      <EditorContent editor={editor}
        className="tiptap-content px-3 py-2.5 text-sm min-h-[180px] max-h-[420px] overflow-y-auto focus:outline-none" />
    </div>
  );
}
